import * as React from "react"

import { useDragPan, type DragPan } from "./use-drag-pan"

export interface ScrollPanOptions {
  /** Off while another gesture owns the pointer, such as drawing a box. */
  enabled?: boolean
  /**
   * The element whose size decides whether there is anywhere to scroll to.
   * Defaults to the viewport's first child; pass the page itself where that
   * child is a fixed-size wrapper (a Radix scroll area's).
   */
  contentRef?: React.RefObject<HTMLElement | null>
}

export interface ScrollPan extends DragPan {
  /** The content overflows the viewport, so a drag has somewhere to go: show a grab cursor. */
  canPan: boolean
}

function overflows(view: HTMLElement): boolean {
  return view.scrollWidth > view.clientWidth + 1 || view.scrollHeight > view.clientHeight + 1
}

/**
 * Drag-to-pan for a viewer that scrolls for real: the drag moves the
 * viewport's scroll position, and a release glides on until an edge.
 *
 * Touch is left to the browser, which already scrolls on a swipe; this is for
 * the mouse, which otherwise has only the scrollbars.
 */
export function useScrollPan(
  viewportRef: React.RefObject<HTMLElement | null>,
  { enabled = true, contentRef }: ScrollPanOptions = {},
): ScrollPan {
  const start = React.useRef({ left: 0, top: 0 })
  const [canPan, setCanPan] = React.useState(false)

  React.useLayoutEffect(() => {
    const view = viewportRef.current
    if (!view || typeof ResizeObserver === "undefined") return
    const measure = (): void => {
      setCanPan(overflows(view))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(view)
    const content = contentRef?.current ?? view.firstElementChild
    if (content) observer.observe(content)
    return () => {
      observer.disconnect()
    }
  }, [viewportRef, contentRef])

  const pan = useDragPan({
    onPress: (event) => {
      const view = viewportRef.current
      if (!enabled || !view || event.pointerType === "touch" || !overflows(view)) return false
      start.current = { left: view.scrollLeft, top: view.scrollTop }
      return true
    },
    onMove: (dx, dy) => {
      const view = viewportRef.current
      if (!view) return
      view.scrollLeft = start.current.left - dx
      view.scrollTop = start.current.top - dy
    },
    onGlide: (vx, vy) => {
      const view = viewportRef.current
      if (!view) return { x: false, y: false }
      const { scrollLeft, scrollTop } = view
      view.scrollLeft = scrollLeft - vx
      view.scrollTop = scrollTop - vy
      return { x: view.scrollLeft !== scrollLeft, y: view.scrollTop !== scrollTop }
    },
  })

  return { ...pan, canPan: enabled && canPan }
}
